import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Wallet, Zap, Package, PlusCircle, Loader } from "lucide-react";

interface RefundSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAcceptStoreCredit: () => void;
  onAcceptOriginal: () => void;
  submitting: boolean;
  productPrice: string | number;
}

const RefundSelectionModal: React.FC<RefundSelectionModalProps> = ({
  isOpen,
  onClose,
  onAcceptStoreCredit,
  onAcceptOriginal,
  submitting,
  productPrice,
}) => {
  const [processingAction, setProcessingAction] = useState<'store_credit' | 'original' | null>(null);
  const numericPrice = typeof productPrice === 'string' ? parseFloat(productPrice.replace(/[^\d.]/g, '')) : productPrice;
  const storeCreditAmount = Math.round(numericPrice * 1.05);

  // Reset processing action when modal closes/opens
  React.useEffect(() => {
    if (!isOpen) setProcessingAction(null);
  }, [isOpen]);

  const handleStoreCreditClick = () => {
    setProcessingAction('store_credit');
    onAcceptStoreCredit();
  };

  const handleOriginalClick = () => {
    setProcessingAction('original');
    onAcceptOriginal();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="tw-fixed tw-inset-0 tw-z-[100] tw-flex tw-items-center tw-justify-center tw-p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="tw-absolute tw-inset-0 tw-bg-black/90 tw-backdrop-blur-xl"
          />
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 30 }}
            className="tw-relative tw-bg-[#0d0d0d] tw-rounded-[32px] tw-w-full tw-max-w-xl tw-overflow-hidden tw-shadow-[0_32px_64px_-16px_rgba(0,0,0,0.6)] tw-border tw-border-white/5 tw-p-10"
          >
            {/* Close Button */}
            <button onClick={onClose} className="tw-absolute tw-top-8 tw-right-8 tw-p-2 tw-text-gray-500 hover:tw-text-white tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-colors">
              <X className="tw-w-6 tw-h-6" />
            </button>
 
            {/* Header */}
            <div className="tw-text-center tw-mb-10">
              <h2 className="tw-text-2xl md:tw-text-3xl tw-font-bold tw-text-white tw-tracking-tight tw-mb-2">Select Your Refund Preference</h2>
              <p className="tw-text-gray-400 tw-text-sm font-medium">Choose how you'd like to receive your settlement</p>
            </div>
 
            <div className="tw-mb-10">
              {/* Card 1: Store Credit */}
              <div 
                className="tw-relative tw-bg-[#1c1c1e] tw-rounded-2xl tw-p-8 tw-border tw-border-white/5"
              >
                <div className="tw-flex tw-items-center tw-gap-4 tw-mb-6">
                  <div className="tw-w-14 tw-h-14 tw-rounded-xl tw-flex tw-items-center tw-justify-center tw-bg-[#00C5FB]/10">
                    <Wallet className="tw-w-7 tw-h-7 tw-text-[#00C5FB]" />
                  </div>
                  <div>
                    <h3 className="tw-text-lg tw-font-bold tw-text-white tw-mb-0.5">Refund (Store Credit)</h3>
                    <p className="tw-text-2xl tw-font-black tw-text-[#00C5FB]">₹{storeCreditAmount.toLocaleString()}</p>
                  </div>
                </div>
                <div className="tw-space-y-3">
                   <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400 tw-text-xs">
                     <Zap className="tw-w-4 tw-h-4 tw-text-[#00C5FB]" />
                     <span>Instant refund to wallet</span>
                   </div>
                   <div className="tw-flex tw-items-center tw-gap-3 tw-text-gray-400 tw-text-xs">
                     <Package className="tw-w-4 tw-h-4 tw-text-[#00C5FB]" />
                     <span>No pickup required</span>
                   </div>
                   <div className="tw-flex tw-items-center tw-gap-3 tw-text-[#00C5FB] tw-font-bold tw-text-xs">
                     <PlusCircle className="tw-w-4 tw-h-4" />
                     <span>Extra 5% bonus included</span>
                   </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="tw-flex tw-flex-col tw-gap-3 tw-mb-4 tw-max-w-md tw-mx-auto">
              <button 
                onClick={handleStoreCreditClick}
                disabled={submitting}
                className="tw-w-full tw-py-4 tw-rounded-xl tw-bg-[#00C5FB] tw-text-black tw-font-bold tw-text-sm tw-transition-all tw-duration-300 tw-cursor-pointer tw-shadow-[0_10px_30px_-5px_rgba(0,197,251,0.4)] hover:tw-bg-cyan-400 tw-border-none tw-flex tw-items-center tw-justify-center tw-gap-2"
              >
                {submitting && processingAction === 'store_credit' ? (
                  <>
                    <Loader className="tw-w-4 tw-h-4 tw-animate-spin" />
                  </>
                ) : (
                  "Proceed with Store Credit"
                )}
              </button>
              <button 
                onClick={handleOriginalClick}
                disabled={submitting}
                className="tw-w-full tw-py-2 tw-rounded-xl tw-bg-transparent tw-text-gray-600 tw-font-bold tw-text-xs tw-transition-all tw-duration-300 tw-cursor-pointer tw-border-none hover:tw-text-gray-400"
              >
                Proceed with Original Refund
              </button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default RefundSelectionModal;
